import { useEffect, useState } from "react";
import {
  Landmark,
  ArrowRight,
  Check,
  FileText,
  AlertTriangle,
  RotateCcw,
  ChevronLeft,
  CreditCard,
  Store,
  Truck,
  ShieldCheck,
} from "lucide-react";

// Local-only walkthrough of the proposed SNAP Online ownership sequence, built
// to screen-share with SEMTAC. The route is gated on import.meta.env.DEV in
// App.tsx so it can never reach a deployed bundle. Nothing here touches the
// API or the database: every number is fixed so the demo can't fail live.

type Line = {
  name: string;
  maker: string;
  price: number;
  wholesale: number;
  eligible: boolean;
  note?: string;
};

const CART: Line[] = [
  { name: "Dozen eggs", maker: "Alty Homestead", price: 7.0, wholesale: 5.25, eligible: true },
  { name: "Baby spinach, 8oz", maker: "Stone Point Farm", price: 5.5, wholesale: 4.0, eligible: true },
  { name: "Ground beef, 1lb", maker: "Meat Wagon", price: 12.0, wholesale: 9.0, eligible: true },
  { name: "Sourdough loaf", maker: "Grammy's", price: 9.0, wholesale: 6.75, eligible: true },
  {
    name: "Beeswax candle",
    maker: "Studio Wick",
    price: 14.0,
    wholesale: 10.5,
    eligible: false,
    note: "Not a food item",
  },
  {
    name: "Hot soup, quart",
    maker: "Grammy's",
    price: 11.0,
    wholesale: 8.25,
    eligible: false,
    note: "Hot prepared food",
  },
];

const ELIGIBLE = CART.filter((l) => l.eligible);
const EBT_TOTAL = ELIGIBLE.reduce((s, l) => s + l.price, 0);
const CARD_TOTAL = CART.filter((l) => !l.eligible).reduce((s, l) => s + l.price, 0);
const ORDER_TOTAL = EBT_TOTAL + CARD_TOTAL;
const COST_BASIS = ELIGIBLE.reduce((s, l) => s + l.wholesale, 0);

const money = (n: number) => `$${n.toFixed(2)}`;

const STEPS = ["Cart", "Stoop buys", "EBT", "Paid", "If it fails"] as const;

export default function DemoEbt() {
  const [step, setStep] = useState(0);
  const [pin, setPin] = useState("");

  // Injected here rather than in index.html so only this route is installable.
  // iOS reads these off the live DOM when you tap Add to Home Screen, which
  // then launches the demo standalone with no Safari address bar.
  useEffect(() => {
    const tags: HTMLMetaElement[] = [
      ["apple-mobile-web-app-capable", "yes"],
      ["apple-mobile-web-app-status-bar-style", "default"],
      ["apple-mobile-web-app-title", "Stoop EBT"],
    ].map(([name, content]) => {
      const m = document.createElement("meta");
      m.name = name;
      m.content = content;
      document.head.appendChild(m);
      return m;
    });
    return () => tags.forEach((m) => m.remove());
  }, []);

  const reset = () => {
    setStep(0);
    setPin("");
  };

  return (
    <div className="mx-auto min-h-screen w-full max-w-md bg-neutral-50 pb-28">
      <header className="sticky top-0 z-10 border-b bg-white/95 px-4 pb-3 pt-[env(safe-area-inset-top)] backdrop-blur">
        <div className="pt-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => window.location.assign("/")}
              className="flex items-center gap-1 rounded-full py-1 pr-2 text-[11px] font-semibold uppercase tracking-widest text-neutral-400 active:text-neutral-700"
            >
              <ChevronLeft className="h-3 w-3" /> Back to app
            </button>
            <button
              onClick={reset}
              className="flex items-center gap-1 rounded-full px-2 py-1 text-[11px] text-neutral-500 active:bg-neutral-100"
            >
              <RotateCcw className="h-3 w-3" /> Restart
            </button>
          </div>
          <div className="mt-2 flex gap-1">
            {STEPS.map((s, i) => (
              <button
                key={s}
                onClick={() => setStep(i)}
                className={`h-1.5 flex-1 rounded-full transition-colors ${
                  i <= step ? "bg-emerald-600" : "bg-neutral-200"
                }`}
                aria-label={s}
              />
            ))}
          </div>
          <div className="mt-1.5 text-xs font-medium text-neutral-600">
            {step + 1} of {STEPS.length} · {STEPS[step]}
          </div>
        </div>
      </header>

      <main className="px-4 pt-5">
        {step === 0 && <StepCart />}
        {step === 1 && <StepTitle />}
        {step === 2 && <StepPin pin={pin} setPin={setPin} />}
        {step === 3 && <StepPaid />}
        {step === 4 && <StepFail />}
      </main>

      <footer className="fixed inset-x-0 bottom-0 mx-auto max-w-md border-t bg-white px-4 pb-[max(env(safe-area-inset-bottom),1rem)] pt-3">
        {step === 2 ? (
          <button
            disabled={pin.length !== 4}
            onClick={() => setStep(3)}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3.5 font-semibold text-white disabled:bg-neutral-200 disabled:text-neutral-400"
          >
            {pin.length === 4 ? (
              <>
                Pay {money(EBT_TOTAL)} with EBT <ArrowRight className="h-4 w-4" />
              </>
            ) : (
              "Enter your 4-digit PIN"
            )}
          </button>
        ) : step < 4 ? (
          <button
            onClick={() => setStep(step + 1)}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3.5 font-semibold text-white"
          >
            {step === 0 && (
              <>
                <Landmark className="h-4 w-4" /> Pay with EBT
              </>
            )}
            {step === 1 && (
              <>
                Continue to EBT payment <ArrowRight className="h-4 w-4" />
              </>
            )}
            {step === 3 && (
              <>
                <AlertTriangle className="h-4 w-4" /> Now show the failure path
              </>
            )}
          </button>
        ) : (
          <button
            onClick={reset}
            className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-neutral-300 py-3.5 font-semibold text-neutral-700"
          >
            <RotateCcw className="h-4 w-4" /> Start over
          </button>
        )}
      </footer>
    </div>
  );
}

function Callout({
  tone = "emerald",
  title,
  children,
}: {
  tone?: "emerald" | "amber" | "neutral";
  title: string;
  children: React.ReactNode;
}) {
  const tones = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
    amber: "border-amber-300 bg-amber-50 text-amber-900",
    neutral: "border-neutral-200 bg-white text-neutral-800",
  };
  return (
    <div className={`rounded-xl border p-3 ${tones[tone]}`}>
      <div className="text-[11px] font-bold uppercase tracking-wide opacity-70">{title}</div>
      <div className="mt-1 text-[13px] leading-relaxed">{children}</div>
    </div>
  );
}

function StepCart() {
  return (
    <div className="space-y-3">
      <h1 className="text-xl font-bold text-neutral-900">Your cart</h1>
      <p className="text-sm text-neutral-500">
        Eligibility is computed per item from its category, erring conservative.
      </p>

      <div className="divide-y rounded-xl border bg-white">
        {CART.map((l) => (
          <div key={l.name} className="flex items-start gap-3 p-3">
            <div className="flex-1">
              <div className="text-[15px] font-medium text-neutral-900">{l.name}</div>
              <div className="text-xs text-neutral-500">{l.maker}</div>
              {l.eligible ? (
                <span className="mt-1.5 inline-flex items-center gap-1 rounded-md bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-800">
                  <Landmark className="h-2.5 w-2.5" /> EBT
                </span>
              ) : (
                <span className="mt-1.5 inline-block text-[11px] text-neutral-400">{l.note}</span>
              )}
            </div>
            <div className="text-[15px] font-semibold tabular-nums text-neutral-900">
              {money(l.price)}
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-1.5 rounded-xl border bg-white p-3 text-sm">
        <Row label="SNAP-eligible" value={money(EBT_TOTAL)} strong tone="emerald" />
        <Row label="Everything else" value={money(CARD_TOTAL)} />
        <div className="border-t pt-1.5">
          <Row label="Order total" value={money(ORDER_TOTAL)} strong />
        </div>
      </div>

      <Callout title="Split tender">
        EBT covers {money(EBT_TOTAL)}. The candle and the hot soup aren't SNAP-eligible, so{" "}
        {money(CARD_TOTAL)} goes to a card in the same checkout. No second trip, no separate order.
      </Callout>
    </div>
  );
}

function StepTitle() {
  return (
    <div className="space-y-3">
      <h1 className="text-xl font-bold text-neutral-900">Stoop purchases the goods</h1>
      <p className="text-sm text-neutral-500">
        This happens the moment you tap "Pay with EBT" — before any payment UI appears.
      </p>

      <div className="space-y-2 rounded-xl border bg-white p-3">
        {ELIGIBLE.map((l) => (
          <div key={l.name} className="flex items-center gap-2.5 text-sm">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-600">
              <Check className="h-3 w-3 text-white" strokeWidth={3} />
            </span>
            <span className="flex-1 text-neutral-700">
              Bought <span className="font-medium text-neutral-900">{l.name}</span> from {l.maker}
            </span>
            <span className="tabular-nums text-neutral-500">{money(l.wholesale)}</span>
          </div>
        ))}
        <div className="flex justify-between border-t pt-2 text-sm font-semibold">
          <span className="flex items-center gap-1.5 text-neutral-900">
            <FileText className="h-3.5 w-3.5" /> PO-2026-0825-118
          </span>
          <span className="tabular-nums">{money(COST_BASIS)}</span>
        </div>
      </div>

      <Callout tone="amber" title="The ownership window is now open">
        Stoop owns {money(COST_BASIS)} of inventory it has <strong>not been paid for</strong>. Title
        has passed. Retail price is set by Stoop. Risk of loss sits with Stoop.
        <div className="mt-2 border-t border-amber-300/60 pt-2">
          Ownership precedes payment. That ordering is the entire point — it creates real exposure
          rather than a contractual label.
        </div>
      </Callout>

      <div className="grid grid-cols-3 gap-2">
        <Fact icon={Store} label="Merchant of record" value="Stoop" />
        <Fact icon={Truck} label="Producers" value="Suppliers" />
        <Fact icon={ShieldCheck} label="Liability" value="Stoop" />
      </div>
    </div>
  );
}

function Fact({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Store;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-xl border bg-white p-2.5 text-center">
      <Icon className="mx-auto h-4 w-4 text-neutral-400" />
      <div className="mt-1 text-[10px] leading-tight text-neutral-500">{label}</div>
      <div className="text-[12px] font-semibold text-neutral-900">{value}</div>
    </div>
  );
}

function StepPin({ pin, setPin }: { pin: string; setPin: (v: string) => void }) {
  return (
    <div className="space-y-3">
      <h1 className="text-xl font-bold text-neutral-900">Pay with EBT</h1>
      <p className="text-sm text-neutral-500">
        Card and PIN fields are hosted by the processor and mount inline. No card number or PIN
        touches a Stoop server, and there's no redirect out of the app.
      </p>

      <div className="rounded-xl border bg-white p-3">
        <label className="text-[11px] font-bold uppercase tracking-wide text-neutral-400">
          EBT card number
        </label>
        <div className="mt-1 flex items-center gap-2 rounded-lg border-2 border-dashed border-neutral-200 bg-neutral-50 px-3 py-2.5">
          <CreditCard className="h-4 w-4 text-neutral-400" />
          <span className="font-mono text-[15px] tracking-wider text-neutral-700">
            6014 •••• •••• 4821
          </span>
        </div>

        <label className="mt-3 block text-[11px] font-bold uppercase tracking-wide text-neutral-400">
          PIN
        </label>
        <div className="mt-1 flex gap-2">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`flex h-12 flex-1 items-center justify-center rounded-lg border-2 text-xl ${
                pin.length > i
                  ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                  : "border-neutral-200 bg-neutral-50"
              }`}
            >
              {pin.length > i ? "•" : ""}
            </div>
          ))}
        </div>
        <p className="mt-1.5 text-[11px] text-neutral-400">Tap the keypad — any 4 digits.</p>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "⌫"].map((k, i) =>
          k === "" ? (
            <div key={i} />
          ) : (
            <button
              key={i}
              onClick={() =>
                k === "⌫" ? setPin(pin.slice(0, -1)) : pin.length < 4 && setPin(pin + k)
              }
              className="rounded-xl border bg-white py-3 text-lg font-semibold text-neutral-800 active:bg-neutral-100"
            >
              {k}
            </button>
          )
        )}
      </div>

      <div className="space-y-1.5 rounded-xl border bg-white p-3 text-sm">
        <Row label="Charging EBT" value={money(EBT_TOTAL)} strong tone="emerald" />
        <Row label="Then card" value={money(CARD_TOTAL)} />
      </div>
    </div>
  );
}

function StepPaid() {
  return (
    <div className="space-y-3">
      <div className="rounded-xl bg-emerald-600 p-4 text-white">
        <Check className="h-6 w-6" strokeWidth={3} />
        <div className="mt-1.5 text-lg font-bold">Order placed</div>
        <div className="text-sm text-emerald-50">Paid to Stoop · Order #4471</div>
      </div>

      <div className="space-y-1.5 rounded-xl border bg-white p-3 text-sm">
        <Row label="EBT SNAP" value={money(EBT_TOTAL)} strong tone="emerald" />
        <Row label="Visa ••4402" value={money(CARD_TOTAL)} />
        <div className="border-t pt-1.5">
          <Row label="Total" value={money(ORDER_TOTAL)} strong />
        </div>
      </div>

      <Callout title="What just happened, in order">
        <ol className="ml-4 list-decimal space-y-1">
          <li>Stoop bought the goods from four producers and took title.</li>
          <li>Stoop sold those goods to the customer at Stoop's retail price.</li>
          <li>The customer paid <strong>Stoop</strong>. EBT settled to Stoop's FNS number.</li>
          <li>Producers get paid {money(COST_BASIS)} as suppliers, on supplier terms.</li>
        </ol>
      </Callout>

      <Callout tone="neutral" title="Returns">
        Defects and spoilage are refunded by Stoop and restocked against Stoop. What we do{" "}
        <strong>not</strong> do is send unsold goods back to the farm — that would be scan-based
        trading, and it's the thing that would make this agency instead of ownership.
      </Callout>
    </div>
  );
}

function StepFail() {
  return (
    <div className="space-y-3">
      <h1 className="text-xl font-bold text-neutral-900">If the payment fails</h1>
      <p className="text-sm text-neutral-500">
        Insufficient balance, wrong PIN, or the customer just closes the app.
      </p>

      <div className="rounded-xl border-2 border-red-200 bg-red-50 p-3">
        <div className="flex items-center gap-2 font-semibold text-red-900">
          <AlertTriangle className="h-4 w-4" /> EBT declined — insufficient balance
        </div>
        <div className="mt-1 text-[13px] text-red-800">
          The customer's order does not complete. Nothing ships.
        </div>
      </div>

      <Callout tone="amber" title="Stoop still owns the goods">
        The purchase from the producers <strong>stands</strong>. Stoop paid {money(COST_BASIS)}, owns{" "}
        {ELIGIBLE.length} items it now has to sell to someone else or write off, and takes the loss
        if it can't.
        <div className="mt-2 border-t border-amber-300/60 pt-2">
          There is no automatic unwind that cancels the producer's sale. An unwind would collapse
          the structure under examination — it would mean the producer carried the demand risk all
          along, which is consignment.
        </div>
      </Callout>

      <div className="rounded-xl border bg-white p-3">
        <div className="text-[11px] font-bold uppercase tracking-wide text-neutral-400">
          Who eats it when it doesn't sell?
        </div>
        <div className="mt-1.5 text-2xl font-bold text-neutral-900">Stoop does.</div>
        <p className="mt-1 text-[13px] leading-relaxed text-neutral-600">
          Exposure is capped honestly — EBT is offered only on maker-confirmed items, against a
          float sized to the decline rate. Capped is not zero, and it shouldn't be. That residual
          risk is what makes the ownership real.
        </p>
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  strong,
  tone,
}: {
  label: string;
  value: string;
  strong?: boolean;
  tone?: "emerald";
}) {
  return (
    <div className="flex justify-between">
      <span className={tone === "emerald" ? "text-emerald-800" : "text-neutral-600"}>{label}</span>
      <span
        className={`tabular-nums ${strong ? "font-semibold" : ""} ${
          tone === "emerald" ? "text-emerald-800" : "text-neutral-900"
        }`}
      >
        {value}
      </span>
    </div>
  );
}
